/**
 * Brand Bible v2 API client — contract §4.4.
 * All routes carry ?tenant_id= (the RLS bridge trigger) and return the score in-band
 * on every mutation. No feature flag — routes are live (contract v1.1 amendment).
 *
 * Integration note: `apiClient` is the repo's axios instance (frontend/src/services/apiClient
 * or equivalent) — adjust the import path on integration; everything else is drop-in.
 */
import { apiClient } from '@/services/apiClient';
import type {
  BbEntry,
  BbTable,
  BbPayloadMap,
  BibleResponse,
  BibleScore,
  EnrichResponse,
  BbSourceRow,
  QuestionnaireInfo,
  QuestionnaireRunSummary,
  QuestionnaireSheetResult,
  GatherEligibility,
  GatherResponse,
  MutationResponse,
} from '@/features/brand-bible-wizard/types';

const BASE = '/brand-bible/v2';

function params(tenantId: string) {
  return { params: { tenant_id: tenantId } };
}

/**
 * Naming seam: the API's table registry uses DB names ("bb_services"); the UI
 * uses short names ("services"). Map here, in BOTH directions, and nowhere else
 * — exported so other v2 consumers (e.g. the file-provenance join, contract
 * B.2 `/extractions`) reuse this instead of re-deriving it.
 */
export const toApiTable = (t: string) => (t.startsWith('bb_') ? t : `bb_${t}`);
export const fromApiTable = (t: string) => (t.startsWith('bb_') ? t.slice(3) : t);
function normalizeBible<T extends { tables?: Record<string, unknown> }>(data: T): T {
  if (data && data.tables) {
    data.tables = Object.fromEntries(
      Object.entries(data.tables).map(([k, v]) => [fromApiTable(k), v]),
    );
  }
  return data;
}

type ApiErrorLike = {
  response?: { status?: number; data?: unknown; headers?: Record<string, unknown> };
} | null | undefined;

/** `detail.code` (FastAPI HTTPException), else a top-level `code`, else null. */
export function apiErrorCode(err: unknown): string | null {
  const data = (err as ApiErrorLike)?.response?.data as
    | { detail?: unknown; code?: unknown }
    | undefined;
  const detail = data?.detail;
  if (detail && typeof detail === 'object' && typeof (detail as { code?: unknown }).code === 'string') {
    return (detail as { code: string }).code;
  }
  return typeof data?.code === 'string' ? data.code : null;
}

/** Seconds to wait after a 429: `detail.retry_after_s`, else the Retry-After header, else null. */
export function apiRetryAfterSeconds(err: unknown): number | null {
  const response = (err as ApiErrorLike)?.response;
  const detail = (response?.data as { detail?: { retry_after_s?: unknown } } | undefined)?.detail;
  if (typeof detail?.retry_after_s === 'number') return detail.retry_after_s;
  const header = response?.headers?.['retry-after'];
  const seconds = typeof header === 'string' || typeof header === 'number' ? Number(header) : NaN;
  return Number.isFinite(seconds) ? seconds : null;
}

export const brandBibleV2Service = {
  /** Full admin view: base tables — unverified/expired included, badged client-side. */
  async getBible(tenantId: string): Promise<BibleResponse> {
    const { data } = await apiClient.get(`${BASE}/bible`, params(tenantId));
    return normalizeBible(data);
  },

  async createEntry<T extends BbTable>(
    tenantId: string,
    table: T,
    payload: BbPayloadMap[T],
  ): Promise<MutationResponse<T>> {
    // Appendix A.6 point 3: 60s belt on entries-create. A fresh tenant's
    // first singleton write used to hit the client's default (~30s) timeout
    // while racing the old find-then-insert window; the server-side race is
    // now closed by the atomic upsert, but the wider timeout stays as
    // defense in depth for genuinely slow networks.
    const { data } = await apiClient.post(
      `${BASE}/entries`,
      { table: toApiTable(table), payload },
      { ...params(tenantId), timeout: 60000 },
    );
    return data;
  },

  async patchEntry<T extends BbTable>(
    tenantId: string,
    table: T,
    id: string,
    payload: Partial<BbPayloadMap[T]>,
  ): Promise<MutationResponse<T>> {
    const { data } = await apiClient.patch(`${BASE}/entries/${toApiTable(table)}/${id}`, { payload }, params(tenantId));
    return data;
  },

  /** Soft delete only (contract §4.4). */
  async deleteEntry<T extends BbTable>(
    tenantId: string,
    table: T,
    id: string,
  ): Promise<MutationResponse<T>> {
    const { data } = await apiClient.delete(`${BASE}/entries/${toApiTable(table)}/${id}`, params(tenantId));
    return data;
  },

  /**
   * Verify — any workspace editor+ (contract §4.3). The server records the REAL acting
   * user from auth state; the client never sends an identity.
   */
  async verifyEntry<T extends BbTable>(
    tenantId: string,
    table: T,
    id: string,
    approveForPublic = false,
  ): Promise<MutationResponse<T>> {
    const { data } = await apiClient.post(
      `${BASE}/entries/${toApiTable(table)}/${id}/verify`,
      { approve_for_public: approveForPublic },
      params(tenantId),
    );
    return data;
  },

  /** Clear verified/public state using the same operator-gated contract. */
  async unverifyEntry<T extends BbTable>(
    tenantId: string,
    table: T,
    id: string,
  ): Promise<MutationResponse<T>> {
    const { data } = await apiClient.post(
      `${BASE}/entries/${toApiTable(table)}/${id}/unverify`,
      null,
      params(tenantId),
    );
    return data;
  },

  async getScore(tenantId: string): Promise<BibleScore> {
    const { data } = await apiClient.get(`${BASE}/score`, params(tenantId));
    return data;
  },

  /**
   * §7.6 — narrow write path for flat brand_bible columns with no v2 home
   * (`legacy_only` in projection_map.json). Server derives the allow-list
   * from the map at request time and 422s any column that isn't legacy_only
   * (already has, or later gains, a v2 home) — never send a §A/typed field
   * through here. PATCH semantics: only the supplied columns are touched.
   */
  async patchLegacyFields(
    tenantId: string,
    payload: Record<string, unknown>,
  ): Promise<{ tenant_id: string; updated_columns: string[] }> {
    const { data } = await apiClient.patch(`${BASE}/legacy-fields`, { payload }, params(tenantId));
    return data;
  },

  /**
   * Enrichment run — synchronous, capped, idempotent (contract §5). Minutes, not seconds:
   * this ONE call needs an extended timeout (same pattern as drive-sync).
   */
  async enrich(tenantId: string): Promise<EnrichResponse> {
    const { data } = await apiClient.post(`${BASE}/enrich`, null, {
      ...params(tenantId),
      timeout: 10 * 60 * 1000,
    });
    return data;
  },

  /** Web gather: drafts from the client's site and GBP. Minutes, not seconds (server deadline 200 s). */
  async gather(tenantId: string): Promise<GatherResponse> {
    const { data } = await apiClient.post(`${BASE}/gather`, null, {
      ...params(tenantId),
      timeout: 10 * 60 * 1000,
    });
    return data;
  },

  async gatherEligibility(tenantId: string): Promise<GatherEligibility> {
    const { data } = await apiClient.get(`${BASE}/gather/eligibility`, params(tenantId));
    return data;
  },

  async getQuestionnaireInfo(tenantId: string): Promise<QuestionnaireInfo> {
    const { data } = await apiClient.get(`${BASE}/questionnaire/info`, params(tenantId));
    return data;
  },

  /** Create the client's questionnaire sheet, or get the existing link. `recreate` (UI-confirmed) trashes the old one. */
  async createQuestionnaireSheet(tenantId: string, recreate = false): Promise<QuestionnaireSheetResult> {
    const { data } = await apiClient.post(`${BASE}/questionnaire/sheet`, { recreate }, params(tenantId));
    return data;
  },

  /** Import the answers in the client's stored sheet as drafts. Minutes, not seconds. */
  async importQuestionnaire(tenantId: string): Promise<QuestionnaireRunSummary> {
    const { data } = await apiClient.post(`${BASE}/questionnaire/import`, null, {
      ...params(tenantId),
      timeout: 10 * 60 * 1000,
    });
    return data;
  },

  /** The .xlsx fallback: multipart `file`; the browser sets the Content-Type (with its boundary). */
  async uploadQuestionnaire(tenantId: string, file: File): Promise<QuestionnaireRunSummary> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post(`${BASE}/questionnaire`, form, { ...params(tenantId), timeout: 10 * 60 * 1000 });
    return data;
  },

  async downloadQuestionnaireTemplate(tenantId: string): Promise<Blob> {
    const { data } = await apiClient.get(`${BASE}/questionnaire/template.xlsx`, { ...params(tenantId), responseType: 'blob' });
    return data;
  },

  /** Provenance (source URL + quote) of the gathered drafts. Never carries the fingerprint. */
  async getSources(tenantId: string): Promise<BbSourceRow[]> {
    const { data } = await apiClient.get(`${BASE}/sources`, params(tenantId));
    return data.sources;
  },

  /** Apply a pending identity value (optionally edited). 409 `stale` / `identity_unverified` propagate. */
  async acceptPendingField(
    tenantId: string,
    field: string,
    body: { value_as_seen: string; value?: string },
  ): Promise<MutationResponse<'identity'>> {
    const { data } = await apiClient.post(`${BASE}/identity/pending/${field}/accept`, body, params(tenantId));
    return data;
  },

  /** Drop a pending identity value; the server remembers it so a rerun does not draft it again. */
  async rejectPendingField(
    tenantId: string,
    field: string,
    valueAsSeen: string,
  ): Promise<{ entry: BbEntry<'identity'> }> {
    const { data } = await apiClient.post(
      `${BASE}/identity/pending/${field}/reject`,
      { value_as_seen: valueAsSeen },
      params(tenantId),
    );
    return data;
  },

  /**
   * Operator-confirmed location merge — keep the verified/primary row, soft-delete
   * the parts-equal twin. Backend route lands on feat/bb-a-merge-api.
   */
  async mergeLocations(tenantId: string, keepId: string, twinId: string): Promise<unknown> {
    const { data } = await apiClient.post(
      `${BASE}/locations/merge`,
      { keep_id: keepId, twin_id: twinId },
      params(tenantId),
    );
    return data;
  },

  /** Restore a twin that mergeLocations soft-deleted. */
  async undoMergeLocation(tenantId: string, twinId: string): Promise<unknown> {
    const { data } = await apiClient.post(
      `${BASE}/locations/merge/undo`,
      { twin_id: twinId },
      params(tenantId),
    );
    return data;
  },
};
